import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type Rent = {
  rentid: number
}
// private module logic //

const id: string = "rent"
const initialState: ApiResult<Rent[]> = { id, state: "init", value: [] }
const rentStream = new BehaviorSubject<ApiResult<Rent[]>>(initialState)
const rentEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let rentMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function sortRent(data: Rent[]): Rent[] {
  data.sort((a, b) => {
    let A = a["code"], B = b["code"]

    if(A > B) return 1
    else if(A < B) return -1
    else return 0
  });

  return data
}

function clear(): void {
  rentStream.next({ id, state: 'ok', value: [] })
  rentMap = {}
}

function get(rentId: number): Rent {
  return rentMap[rentId]
}

function refresh(): Promise<any> {
  const currentValue = rentStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchRent(propertyid)
}

function fetchRent(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<Rent[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  rentStream.next({ id, state: 'busy', value: rentStream.getValue().value })

  let url = `rent?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `rent?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: Rent[]) => {
    _.forEach(records, (rent) => (rentMap[rent.rentid] = rent))
    // merge results unique by rentid
    rentStream.next({ id, state: 'ok', value: sortRent(records) })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number, defaults: {}): Promise<Rent | { message: string }> {
  const currentValue = rentStream.getValue().value
  rentStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    amtnew: null,
    amtrenew: null,
    amttype: defaults["rentAmtType"],
    inputtype: "basic",
    name: "",
    inflationdetail: {
      inflationMode: "namedItem",
      inflationNamedItemId: _.get(defaults, "rentInflation.inflationNamedItemId")
    },
    rentdetail: null
  }

  return Client.post(`rent`, payload)
    .then((rent: Rent) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, rent.rentid),
        redofunc: () => create(propertyid, defaults)
      })
      rentMap[rent.rentid] = rent
      // merge results unique by rentid
      const currentValue = rentStream.getValue().value
      rentStream.next({
        id,
        state: 'ok',
        value: _.unionBy([rent], currentValue, 'rentid')
      })
      rentEventStream.next({ type: 'RENT_CREATED', payload: rent })
      return rent
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      rentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchRent(propertyid)
      return err
    })
}

function copy(propertyid:number, rentid:number, code:string) {
  const currentValue = rentStream.getValue().value;
  rentStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'rent', recordid: rentid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((rent: Rent) => {
      const newRecord = rent[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.rentid),
        redofunc: () => copy(propertyid, rentid, code)
      });
      rentMap[newRecord.rentid] = newRecord
      // merge results unique by rentid
      const currentValue = rentStream.getValue().value
      rentStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'rentid').sort((a, b) => alphanum(a.code, b.code))
      })
      rentEventStream.next({ type: 'RENT_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      rentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRent(propertyid)
      return err
    })
}

function copies(propertyid: number, rentid: number, count: number) {
  const currentValue = rentStream.getValue().value;
  rentStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'rent', recordid: rentid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((rents: Rent[]) => {
      push_undo({
        undofunc: () => _.forEach(rents, newRecord => deleteOne(propertyid, newRecord.rentid)),
        redofunc: () => copies(propertyid, rentid, count)
      });
      _.forEach(rents, newRecord => rentMap[newRecord.rentid] = newRecord)
      // merge results unique by rentid
      const currentValue = rentStream.getValue().value
      rentStream.next({
        id,
        state: 'ok',
        value: _.unionBy(rents, currentValue, 'rentid').sort((a, b) => alphanum(a.code, b.code))
      })
      rentEventStream.next({ type: 'RENT_COPIED', payload: rents })
      return rents
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      rentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRent(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  rentid: number,
): Promise<boolean | { message: string }> {
  const currentValue = rentStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, rentid)
  const refreshFn = () => fetchRent(propertyid)
  if (!(await handleReferences('rent', rentid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted rentid
  const value = _(currentValue)
    .filter((p) => p.rentid !== rentid)
    .value()
  value.forEach((p) => (rentMap[p.rentid] = p))

  rentStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(rentid).then(() =>
  Client.del(`rent?rentid=eq.${rentid}`)
    .then((rent: Rent) => {
      delete rentMap[rent.rentid]
      push_undo({
        undofunc: () => {
          Client.post('rent', rent)
          rentMap[rent.rentid] = rent
          rentStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, rentid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      rentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRent(propertyid)
      return err
    })
  )
}

function patch(rent: {
  rentid: number
}): Promise<Rent | { message: string }> {
  const currentValue = rentStream.getValue().value
  const rentid: number = rent.rentid
  const currentrent = _.find(currentValue, { rentid })
  const changeset = diff_obj(rent, currentrent)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentrent),
    redofunc: () => patch(rent)
  })

  // optimistic update
  rentStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {rentid}), rent)
  })

  return _patches_.watch(rentid, Client.patch(`rent?rentid=eq.${rentid}`, changeset)
    .then((mf: Rent[]) => {
      _.forEach(mf, (rent) => (rentMap[rent.rentid] = rent))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      rentStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentrent, 'propertyid')
      if (propertyid) fetchRent(propertyid)
      return err
    }))

}

// public module interface //

const rentService = {
  stream: () => rentStream.asObservable(),
  value: () => rentStream.getValue().value,
  eventStream: () => rentEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  get,
  fetchRent,
  patch,
  refresh
}

export default rentService
